import _ from 'lodash';

import { FIELD_TYPES, FORM_EDITOR_MODE, FORM_STATUS } from '../../../common/globals';
import generateElementStrategies from './generateElementStrategies';
import formModificationsChecker from './formModificationsChecker';

const MOVE_DIRECTION = {
  UP: 'up',
  DOWN: 'down'
};

const updatePropertyById = (state, id, name, value) => {
  const { properties } = state.form.JSONSchema;

  const updatedProperties = _.cloneDeep(properties);

  if (id && _.has(updatedProperties, id)) {
    updatedProperties[id][name] = value;
  }

  return updatedProperties;
};

const initializeFormSuccess = (state, payload) => {
  const updatedState = _.cloneDeep(state);

  const {
    form,
    teamName
  } = payload;

  updatedState.isInitializing = false;
  updatedState.isDeleted = false;
  updatedState.teamName = teamName;
  updatedState.selectedElement = null;
  updatedState.mode = FORM_EDITOR_MODE.EDIT_FORM;
  updatedState.form = form;
  updatedState.form.UISchema = _.isEmpty(updatedState.form.UISchema)
    ? { 'ui:order': [] }
    : updatedState.form.UISchema;
  updatedState.form.JSONSchema.description = _.defaultTo(
    updatedState.form.JSONSchema.description,
    ''
  );

  // Default and description field dont come in the payload if they have no value in the backend
  // and  causes edge cases to fail since in UI the default is ''. We need to standarize it
  // I chose to mutate he object inplace since it's already the state clone
  // to avoid generating more clones of the of each element and also the array.
  const {
    properties
  } = updatedState.form.JSONSchema;
  const propertiesKeys = _.keys(properties);

  propertiesKeys.forEach((propertyKey) => {
    const element = properties[propertyKey];
    element.description = _.defaultTo(element.description, '');

    // file and date fields either have a valid value or no value.
    // Empty strings cause value type errors.
    if (element.format !== 'data-url' && element.format !== 'date-time') {
      element.default = _.defaultTo(element.default, '');
    }
  });

  // To perform dirty checks
  updatedState.pristineForm = _.cloneDeep(updatedState.form);
  updatedState.isModified = false;

  return updatedState;
};

const updateFormElement = (state, payload) => {
  const updatedState = _.cloneDeep(state);

  const { id } = updatedState.selectedElement;
  const updatedSelectedElement = updatedState.selectedElement;

  const { target } = payload;
  const { name, value } = target;
  
  // Route UI-specific updates (e.g., ui:placeholder) to UISchema, not JSONSchema
  if (_.startsWith(name, 'ui:')) {
    const updatedForm = updatedState.form;
    const updatedUISchema = _.cloneDeep(updatedForm.UISchema);
    const elementUISchema = _.defaultTo(updatedUISchema[id], {});
    elementUISchema[name] = value;
    updatedUISchema[id] = elementUISchema;
    updatedForm.UISchema = updatedUISchema;
    // Store a mirror for controlled input rendering without plumbing uiSchema prop
    if (name === 'ui:placeholder') {
      updatedSelectedElement.uiPlaceholder = value;
    }
    updatedState.form = updatedForm;
    updatedState.selectedElement = updatedSelectedElement;
    updatedState.isModified = formModificationsChecker.check(updatedState);
    return updatedState;
  }
  
  const updatedProperties = updatePropertyById(state, id, name, value);
  updatedSelectedElement[name] = value;

  const updatedForm = updatedState.form;
  const updatedFormData = updatedForm.formData;

  if (updatedSelectedElement.format !== 'data-url' && updatedSelectedElement.format !== 'date-time') {
    let formDataValue = updatedSelectedElement.default;
    if (updatedSelectedElement.type === 'integer') {
      const intValue = parseInt(updatedSelectedElement.default, 10);
      const safeIntValue = _.isNaN(intValue) ? 0 : intValue;

      formDataValue = safeIntValue;

      if (name === 'default') {
        updatedProperties[id].default = safeIntValue;
      }
    }

    updatedFormData[id] = formDataValue;
  }

  updatedForm.JSONSchema.properties = updatedProperties;
  updatedForm.formData = updatedFormData;

  updatedState.selectedElement = updatedSelectedElement;
  updatedState.form = updatedForm;
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const updateFormElementType = (state, payload) => {
  const { type } = payload;
  
  const updatedState = _.cloneDeep(state);
  const {
    properties: updatedProperties
  } = updatedState.form.JSONSchema;
  const {
    UISchema,
    formData
  } = updatedState.form;

  let updatedSelectedElement = _.cloneDeep(state.selectedElement);
  let updatedUISchema = _.cloneDeep(UISchema);
  let updatedFormData = _.cloneDeep(formData);
  const { id } = updatedSelectedElement;
  
  const {
    SHORT_TEXT,
    PARAGRAPH,
    DATE,
    NUMBER,
    SELECT,
    CHECKBOXES,
    SINGLE_FILE,
    MULTIPLE_FILES
  } = FIELD_TYPES;

  switch (type) {
    case SHORT_TEXT:
    {
      updatedSelectedElement.type = 'string';
      updatedSelectedElement.default = '';
      delete updatedSelectedElement.format;
      updatedProperties[id].type = 'string';
      updatedProperties[id].default = '';
      delete updatedProperties[id].format;
      break;
    }

    case PARAGRAPH:
    {
      updatedSelectedElement.type = 'string';
      updatedSelectedElement.default = '';
      updatedSelectedElement.format = 'textarea';
      updatedProperties[id].type = 'string';
      updatedProperties[id].default = '';
      updatedProperties[id].format = 'textarea';
      break;
    }

    case DATE:
    {
      updatedSelectedElement.type = 'string';
      updatedSelectedElement.format = 'date-time';
      updatedSelectedElement.default = undefined;
      updatedProperties[id].type = 'string';
      updatedProperties[id].format = 'date-time';
      updatedProperties[id].default = undefined;
      break;
    }

    case NUMBER:
    {
      updatedSelectedElement.type = 'integer';
      delete updatedSelectedElement.format;
      updatedSelectedElement.default = 0;
      updatedProperties[id].type = 'integer';
      delete updatedProperties[id].format;
      updatedProperties[id].default = 0;

      // If there is form data update it to new default value for type
      if (!_.isUndefined(updatedFormData)) {
        updatedFormData[id] = 0;
      }

      break;
    }

    case SELECT:
    {
      updatedSelectedElement.type = 'string';
      updatedProperties[id].type = 'string';
      updatedSelectedElement.enum = _.clone(updatedSelectedElement.items.enum);
      updatedProperties[id].enum = _.clone(updatedSelectedElement.items.enum);
      updatedSelectedElement = _.omit(updatedSelectedElement, 'items');
      updatedProperties[id] = _.omit(updatedProperties[id], 'items');
      updatedSelectedElement = _.omit(updatedSelectedElement, 'uniqueItems');
      updatedProperties[id] = _.omit(updatedProperties[id], 'uniqueItems');
      updatedUISchema = _.omit(updatedUISchema, id);

      // If there is a default value set update it or it will be incompatible
      if (!_.isUndefined(updatedSelectedElement.default)) {
        [updatedSelectedElement.default] = updatedSelectedElement.default;
        [updatedProperties[id].default] = updatedProperties[id].default;
      }

      break;
    }
    
    case CHECKBOXES:
    {
      updatedSelectedElement.type = 'array';
      updatedProperties[id].type = 'array';
      updatedSelectedElement.items = {
        type: 'string',
        enum: _.clone(updatedSelectedElement.enum)
      };
      updatedProperties[id].items = {
        type: 'string',
        enum: _.clone(updatedSelectedElement.enum)
      };
      updatedSelectedElement = _.omit(updatedSelectedElement, 'enum');
      updatedProperties[id] = _.omit(updatedProperties[id], 'enum');
      updatedSelectedElement.uniqueItems = true;
      updatedProperties[id].uniqueItems = true;
      updatedSelectedElement = _.omit(updatedSelectedElement, 'enum');
      updatedProperties[id] = _.omit(updatedProperties[id], 'enum');
      updatedUISchema[id] = {
        'ui:widget': 'checkboxes'
      };

      // If there is a default value set update it or it will be incompatible
      if (!_.isUndefined(updatedSelectedElement.default)) {
        updatedSelectedElement.default = [updatedSelectedElement.default];
        updatedProperties[id].default = [updatedProperties[id].default];
      }

      break;
    }

    case SINGLE_FILE:
      updatedSelectedElement.type = 'string';
      updatedProperties[id].type = 'string';
      updatedSelectedElement.format = 'data-url';
      updatedProperties[id].format = 'data-url';
      updatedSelectedElement = _.omit(updatedSelectedElement, 'items');
      updatedProperties[id] = _.omit(updatedProperties[id], 'items');
      break;

    case MULTIPLE_FILES:
      updatedSelectedElement.type = 'array';
      updatedProperties[id].type = 'array';
      updatedSelectedElement.format = 'data-url';
      updatedProperties[id].format = 'data-url';
      updatedSelectedElement.items = {
        type: 'string',
        format: 'data-url'
      };
      updatedProperties[id].items = {
        type: 'string',
        format: 'data-url'
      };
      break;

    default:
      break;
  }
  // If there is form data clear it because it will be incompatible on format change
  if (!_.isUndefined(updatedFormData)) {
    updatedFormData = _.omit(updatedFormData, id);
  }

  const updatedForm = updatedState.form;
  updatedForm.JSONSchema.properties = updatedProperties;
  updatedForm.UISchema = updatedUISchema;
  updatedForm.formData = updatedFormData;
  
  updatedState.form = updatedForm;
  updatedState.selectedElement = updatedSelectedElement;
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const moveItem = (array, elementId, direction) => {
  const element = _.find(array, item => item === elementId);
  const elementPosition = _.findIndex(array, item => item === elementId);

  if (!element || elementPosition === -1) {
    return array;
  }

  let result = _.cloneDeep(array);

  if (direction === MOVE_DIRECTION.UP && elementPosition > 0) {
    result = _.without(result, element);
    const newPosition = elementPosition - 1;
    result.splice(newPosition, 0, element);
  }

  if (direction === MOVE_DIRECTION.DOWN && elementPosition < _.size(array) - 1) {
    result = _.without(result, element);
    const newPosition = elementPosition + 1;
    result.splice(newPosition, 0, element);
  }

  return result;
};

const moveFormElementUp = (state, payload) => {
  const updatedState = _.cloneDeep(state);
  const updatedForm = updatedState.form;
  let updatedOrder = _.clone(updatedForm.UISchema['ui:order']);
  
  const { id } = payload;

  updatedOrder = moveItem(updatedOrder, id, MOVE_DIRECTION.UP);
  updatedForm.UISchema['ui:order'] = updatedOrder;

  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const moveFormElementDown = (state, payload) => {
  const updatedState = _.cloneDeep(state);
  const updatedForm = updatedState.form;
  let updatedOrder = _.clone(updatedForm.UISchema['ui:order']);
  
  const { id } = payload;

  updatedOrder = moveItem(updatedOrder, id, MOVE_DIRECTION.DOWN);
  updatedForm.UISchema['ui:order'] = updatedOrder;

  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const removeFormElement = (state, payload) => {
  const { id: elementId } = payload;

  const updatedState = _.cloneDeep(state);
  const updatedForm = updatedState.form;
  const updatedElements = _.omit(
    updatedForm.JSONSchema.properties,
    elementId
  );
  const updatedRequired = _.filter(
    updatedForm.JSONSchema.required,
    item => item !== elementId
  );
  const updatedElementOrder = _.filter(
    updatedForm.UISchema['ui:order'],
    item => item !== elementId
  );

  updatedForm.JSONSchema.properties = updatedElements;
  updatedForm.UISchema['ui:order'] = updatedElementOrder;
  updatedForm.JSONSchema.required = updatedRequired;

  updatedState.form = updatedForm;
  updatedState.mode = FORM_EDITOR_MODE.EDIT_FORM;
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const updateFormDetails = (state, payload) => {
  const updatedState = _.cloneDeep(state);
  const updatedForm = updatedState.form;
  const {
    name,
    value
  } = payload.target;

  updatedForm[name] = value;

  const {
    JSONSchema
  } = updatedForm;
  JSONSchema[name] = value;

  updatedState.form = updatedForm;
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const generateElement = (state, payload) => {
  const {
    newElementType
  } = payload;

  const strategy = generateElementStrategies[newElementType];

  const updatedState = strategy(state);
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const changeRequiredFieldStatus = (state, payload) => {
  const updatedState = _.cloneDeep(state);
  const updatedForm = updatedState.form;
  const updatedSelectedElement = updatedState.selectedElement;
  
  const { id } = payload;

  const {
    required
  } = updatedForm.JSONSchema;

  const isRequired = _.isUndefined(
    _.find(required, item => item === id)
  );
  
  let updatedRequired;

  if (isRequired) {
    updatedRequired = _.concat(required, [id]);

    if (updatedForm.JSONSchema.properties[id].type === 'array') {
      updatedForm.JSONSchema.properties[id].minItems = 1;
    }
  }
  else {
    updatedRequired = _.filter(required, item => item !== id);
    updatedForm.JSONSchema.properties[id] = _.omit(updatedForm.JSONSchema.properties[id], 'minItems');
  }

  updatedForm.JSONSchema.required = updatedRequired;
  updatedSelectedElement.required = isRequired;

  updatedState.form = updatedForm;
  updatedState.selectedElement = updatedSelectedElement;

  updatedState.isModified = formModificationsChecker.check(updatedState);
  
  return updatedState;
};

const updateEditorMode = (state, payload) => {
  const updatedState = _.cloneDeep(state);
  const {
    mode
  } = payload;

  if (mode === FORM_EDITOR_MODE.EDIT_FORM) {
    updatedState.selectedElement = null;
  }

  updatedState.mode = mode;

  return updatedState;
};

const changeElementOptions = (state, payload) => {
  const updatedState = _.cloneDeep(state);

  const updatedForm = updatedState.form;
  const updatedSelectedElement = updatedState.selectedElement;
  const updatedFormData = updatedForm.formData;
  
  let name = '';
  let value = [];
  
  const {
    optionsText
  } = payload;

  const updatedOptions = _.split(optionsText, '\n');
  let formDataValue;
  if (updatedSelectedElement.type === 'array') {
    name = 'items';
    value = {
      type: 'string',
      enum: updatedOptions
    };
    formDataValue = [updatedOptions[0]];
  }
  else {
    name = 'enum';
    value = updatedOptions;
    [formDataValue] = updatedOptions;
  }

  const {
    id
  } = updatedSelectedElement;

  updatedFormData[id] = formDataValue;

  updatedForm.JSONSchema.properties[id][name] = value;
  updatedForm.JSONSchema.properties[id].default = formDataValue;
  updatedSelectedElement[name] = value;
  updatedSelectedElement.default = formDataValue;

  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const saveForm = (state) => {
  const updatedState = _.cloneDeep(state);
  
  updatedState.isSaving = false;
  updatedState.isModified = false;
  updatedState.form.status = FORM_STATUS.DRAFT;
  updatedState.pristineForm = _.cloneDeep(updatedState.form);

  return updatedState;
};

const updateTasksAssignedTo = (state, assignees) => {
  const updatedState = _.cloneDeep(state);

  updatedState.form.assignTasksTo = [...assignees];
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const updateTaskDeadline = (state, deadline) => {
  const updatedState = _.cloneDeep(state);

  updatedState.form.tasksDeadline = deadline;
  updatedState.isModified = formModificationsChecker.check(updatedState);
  
  return updatedState;
};

const updateTaskSummary = (state, taskSummary) => {
  const updatedState = _.cloneDeep(state);

  updatedState.form.taskSummary = taskSummary;
  updatedState.isModified = formModificationsChecker.check(updatedState);
  
  return updatedState;
};

const updateHeaderImage = (state, headerImage) => {
  const updatedState = _.cloneDeep(state);

  updatedState.form.headerImage = headerImage;
  updatedState.isModified = formModificationsChecker.check(updatedState);
  
  return updatedState;
};

const updateConfirmationText = (state, confirmationText) => {
  const updatedState = _.cloneDeep(state);

  updatedState.form.confirmationText = confirmationText;
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const updateButtonLabel = (state, buttonLabel) => {
  const updatedState = _.cloneDeep(state);

  updatedState.form.buttonLabel = buttonLabel;
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

const updateButtonColor = (state, buttonColor) => {
  const updatedState = _.cloneDeep(state);

  updatedState.form.buttonColor = buttonColor;
  updatedState.isModified = formModificationsChecker.check(updatedState);

  return updatedState;
};

export default {
  initializeFormSuccess,
  updateFormElement,
  updateFormElementType,
  moveFormElementUp,
  moveFormElementDown,
  removeFormElement,
  updateFormDetails,
  generateElement,
  changeRequiredFieldStatus,
  updateEditorMode,
  changeElementOptions,
  saveForm,
  updateTasksAssignedTo,
  updateTaskDeadline,
  updateTaskSummary,
  updateHeaderImage,
  updateConfirmationText,
  updateButtonLabel,
  updateButtonColor
};
